import circle from '@turf/circle';
import { DeviceConfigGeoAlert } from '@/api';

export type LngLat = {
    longitude: number;
    latitude: number;
};

/** A bounding box as [[minLng, minLat], [maxLng, maxLat]]. */
export type Bounds = [[number, number], [number, number]];

/**
 * Builds the closed outer ring (loop of [lng, lat] pairs) describing a
 * geofence's shape for display: the circle outline for a circle, or the closed
 * polygon for a polygon. Returns null when the slot has no drawable shape.
 */
export function geoAlertRing(
    slot: DeviceConfigGeoAlert
): [number, number][] | null {
    if (slot.shape === 'circle') {
        const centre = slot.points?.[0];
        if (!centre || slot.radius == null || slot.radius <= 0) {
            return null;
        }
        return circleRing(
            { longitude: centre.longitude, latitude: centre.latitude },
            slot.radius
        );
    }
    if (slot.shape === 'polygon') {
        const points = slot.points ?? [];
        if (points.length < 3) {
            return null;
        }
        const ring = points.map(
            (p) => [p.longitude, p.latitude] as [number, number]
        );
        ring.push(ring[0]); // close the loop
        return ring;
    }
    return null;
}

/** Computes the bounding box of a ring of [lng, lat] coordinates. */
export function ringBounds(ring: [number, number][]): Bounds {
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    ring.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
    });
    return [
        [minLng, minLat],
        [maxLng, maxLat]
    ];
}

/**
 * Builds the outer ring (a closed loop of [lng, lat] pairs) of a circle of the
 * given radius in metres, centred on `centre`. Used to preview a circular
 * geofence on the map via a GeoJSON polygon Source/Layer.
 */
export function circleRing(
    centre: LngLat,
    radiusMeters: number
): [number, number][] {
    const feature = circle([centre.longitude, centre.latitude], radiusMeters, {
        steps: 64,
        units: 'meters'
    });
    return feature.geometry.coordinates[0] as [number, number][];
}
